import React from 'react';

// 1. Agregamos 'outline' a los tipos permitidos
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'accent' | 'ghost' | 'danger' | 'success' | 'outline';
    size?: 'sm' | 'md' | 'lg';
    isLoading?: boolean;
    icon?: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
                                           children,
                                           variant = 'primary',
                                           size = 'md',
                                           isLoading = false,
                                           icon,
                                           className = '',
                                           disabled,
                                           ...props
                                       }) => {

    // Estilos Base
    const baseStyles = "inline-flex items-center justify-center rounded-xl font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

    // Tamaños
    const sizes = {
        sm: "px-3 py-1.5 text-xs",
        md: "px-4 py-2 text-sm",
        lg: "px-6 py-3 text-base",
    };

    // 2. Definimos los estilos para 'outline' y las demás variantes
    const variants = {
        // --st-accent-primary-hover is the hover tone defined in index.css (also used by .st-btn-primary)
        primary: "bg-[var(--st-accent-primary)] text-white hover:bg-[var(--st-accent-primary-hover)] focus:ring-[var(--st-accent-primary)] border border-transparent shadow-sm hover:shadow-md",
        secondary: "bg-[var(--st-bg-secondary)] text-[var(--st-text-primary)] hover:bg-[var(--st-bg-tertiary)] border border-[var(--st-border)]",
        accent: "bg-gradient-to-r from-[var(--st-accent-primary)] to-[var(--st-accent-secondary)] text-white hover:opacity-90 shadow-md border border-transparent",
        ghost: "bg-transparent text-[var(--st-text-secondary)] hover:text-[var(--st-text-primary)] hover:bg-[var(--st-bg-tertiary)]",
        danger: "bg-[var(--st-error)] text-white hover:bg-red-600 focus:ring-red-500 border border-transparent",
        success: "bg-[var(--st-success)] text-white hover:bg-green-600 focus:ring-green-500 border border-transparent",

        // Estilo Outline Genérico
        outline: "bg-transparent border-2 border-[var(--st-border)] text-[var(--st-text-primary)] hover:border-[var(--st-accent-primary)] hover:text-[var(--st-accent-primary)]"
    };

    return (
        <button
            className={`
                ${baseStyles} 
                ${sizes[size]} 
                ${variants[variant]} 
                ${isLoading ? 'cursor-wait opacity-80' : ''} 
                ${className}
            `}
            disabled={disabled || isLoading}
            {...props}
        >
            {isLoading && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            )}

            {!isLoading && icon && <span className="mr-2">{icon}</span>}

            {children}
        </button>
    );
};

export default Button;